import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { applyHexView, closeHexPopup, loadHexagonsLayer, loadPriceChangeLayer, MeanPriceViews, PriceChangeViews } from '../utils/mapHelpers';
import { MeanPriceLegend } from './MeanPriceLegend';
import { MapLayerToggle, HexLayer } from './MapLayerToggle';
import { MapUnitToggle, HexUnit } from './MapUnitToggle';
import { PriceChangeLegend } from './PriceChangeLegend';
import './Map.css';

function Map() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const layerRef = useRef<HexLayer>('meanPrice');
  const unitRef = useRef<HexUnit>('nominal');
  const [layer, setLayer] = useState<HexLayer>('meanPrice');
  const [unit, setUnit] = useState<HexUnit>('nominal');
  const [priceStops, setPriceStops] = useState<MeanPriceViews>({ nominal: [], perSqft: [] });
  const [changeStops, setChangeStops] = useState<PriceChangeViews>({ nominal: [], perSqft: [] });

  useEffect(() => {
    // If map already exists, don't initialize again
    if (map.current) return;

    if (!mapContainer.current) {
      console.warn('Map container not available yet');
      return;
    }

    // Durham, NC coordinates
    const durhamCoords = {
      lat: 35.9940,
      lng: -78.8986
    };

    let cancelled = false;

    try {
      // Use OpenStreetMap style with street and city labels
      // Using OpenFreeMap Liberty style which includes OSM vector data with labels
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: 'https://tiles.openfreemap.org/styles/liberty',
        center: [durhamCoords.lng, durhamCoords.lat],
        zoom: 12,  // Better zoom level for Durham city view
      });


      // Wait for map to load before adding controls and GeoJSON layer 
      map.current.on('load', () => {
        // Add navigation controls
        if (map.current) {
          map.current.resize();
          map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

          const loadedMap = map.current;
          // Load both hex layers. The toggle picks which fill is visible.
          loadHexagonsLayer(loadedMap).then((stops) => {
            if (!cancelled) {
              setPriceStops(stops);
              applyHexView(loadedMap, layerRef.current, unitRef.current);
            }
          });
          loadPriceChangeLayer(loadedMap).then((stops) => {
            if (!cancelled) {
              setChangeStops(stops);
              applyHexView(loadedMap, layerRef.current, unitRef.current);
            }
          });
        }
      });
    } catch (error) {
      console.error('Error initializing map:', error);
      map.current = null;
    }

    return () => {
      cancelled = true;
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  const chooseLayer = (next: HexLayer) => {
    layerRef.current = next;
    setLayer(next);
    if (map.current) {
      applyHexView(map.current, next, unitRef.current);
    } else {
      closeHexPopup();
    }
  };

  const chooseUnit = (next: HexUnit) => {
    unitRef.current = next;
    setUnit(next);
    if (map.current) {
      applyHexView(map.current, layerRef.current, next);
    } else {
      closeHexPopup();
    }
  };

  const perSqft = unit === 'perSqft';

  return (
    <div className="map-frame">
      <div ref={mapContainer} className="map-canvas" />
      <div className="map-controls">
        <MapLayerToggle layer={layer} onChange={chooseLayer} />
        <MapUnitToggle unit={unit} onChange={chooseUnit} />
      </div>
      {layer === 'meanPrice'
        ? <MeanPriceLegend stops={priceStops[unit]} perSqft={perSqft} />
        : <PriceChangeLegend stops={changeStops[unit]} perSqft={perSqft} />}
    </div>
  );
}

export default Map;
